import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dialog } from "antd-mobile";
import {
  getHotelOrderDetail,
  cancelHotelOrder,
  type OrderDetailData,
} from "@/api/hotel";
import { REFUND_STATUS_MAP, type RefundInfo } from "@/api/order";
import { cancelTourOrder } from "@/api/tourOrder";
import { toastError, toastSuccess, toastWarning } from "@/utils/toast";

// ── Components ──────────────────────────────────────────────
import { ProgressBarByStep } from "@/components/ProgressBar";
import ProductActionButtons from "../components/ProductActionButtons";
import InvoiceStatusNotice from "../components/InvoiceStatusNotice";

// ── Sections ────────────────────────────────────────────────
import HotelSection from "./sections/HotelSection";
import CarSection from "./sections/CarSection";
import RouteSection from "./sections/RouteSection";
import GuideSection from "./sections/GuideSection";

// ── Shared Modals ──────────────────────────────────────────
import { AfterSaleModal, ReviewModal, InvoiceModal } from "@/components/modals";
import RefundInfoSection from "../components/RefundInfoSection";
import VoucherServiceSection from "../components/VoucherServiceSection";

// ── Hotel-specific Modals ─────────────────────────────────────
import ContractSignModal from "../modals/ContractSignModal";
import CancelOrderModal from "../modals/CancelOrderModal";
import CarLocationModal from "../modals/CarLocationModal";
import ChangePickupModal from "../modals/ChangePickupModal";
import RouteRefundModal from "../modals/RouteRefundModal";

function resolveMainStatusStep(
  status: number,
  hasRefund: boolean,
  refundStatus: number,
) {
  if ((hasRefund && refundStatus !== 30) || status === 5 || status === 6) {
    return -1;
  }
  let step = 0;
  if (status < 3 || status === 7) {
    return status;
  }
  if (status === 4) {
    return 3;
  }
  return step;
}

/** 将 API 数据映射为组件所需格式 */
function mapHotelOrderToUI(data: OrderDetailData) {
  const refundInfo = data.refund_info as RefundInfo | undefined;
  const hasRefund = refundInfo?.has_refund === 1;
  const invoiceInfo = data as {
    has_invoice?: number | string | null;
    invoice_status?: number | string | null;
  };
  return {
    id: String(data.id),
    orderNo: data.order_no,
    type: "single" as const,
    mainStatus:
      hasRefund && refundInfo?.refund_status !== 30
        ? (REFUND_STATUS_MAP[refundInfo!.refund_status] ?? data.status_text)
        : data.status_text,
    mainStatusStep: resolveMainStatusStep(
      data.status,
      hasRefund,
      refundInfo?.refund_status ?? 0,
    ),
    total: Number(data.total_amount || 0),
    time: data.add_time ?? "",
    destination: "酒店",
    travelers: 1,
    productType: "hotel" as const,
    productName: data.hotel?.name ?? "",
    productSpec: `${data.room?.name ?? ""} · ${data.stay_period ?? ""}`,
    productDate: `${data.check_in_date ?? ""} ~ ${data.check_out_date ?? ""}`,
    productSubStatus: hasRefund
      ? (REFUND_STATUS_MAP[refundInfo!.refund_status] ?? "售后中")
      : data.status_text,
    productSubStatusColor: hasRefund ? "#DC2626" : "#2563EB",
    productSubStatusBg: hasRefund ? "#FEE2F2" : "#EFF6FF",
    hasRefund: refundInfo?.has_refund,
    refundStatus: refundInfo?.refund_status,
    refundInfo: refundInfo,
    hasComment: data.has_comment,
    hasInvoice: invoiceInfo.has_invoice,
    invoiceStatus: invoiceInfo.invoice_status,
  };
}

// ─── Main Page ─────────────────────────────────────────────
export default function OrderDetailHotelPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const locState = location.state as {
    orderId?: number;
    productType?: string;
    openContract?: boolean;
  } | null;
  const orderId = locState?.orderId;
  const productType = "hotel";

  // 状态
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<Record<string, any> | null>(null);
  const [rawData, setRawData] = useState<OrderDetailData | null>(null);
  const [showFee, setShowFee] = useState(false);
  const [showContract, setShowContract] = useState(
    locState?.openContract === true,
  );
  const [contractSigned, setContractSigned] = useState(false);
  const [displayStatus, setDisplayStatus] = useState("");
  const [displayStep, setDisplayStep] = useState(0);
  const [modal, setModal] = useState<string | null>(null);

  // 接口数据
  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    getHotelOrderDetail(orderId)
      .then((res) => {
        if (res.data.code !== 200)
          throw new Error(res.data.message || "获取订单失败");
        const ui = mapHotelOrderToUI(res.data.data);
        setOrder(ui);
        setRawData(res.data.data);
        setDisplayStatus(ui.mainStatus);
        setDisplayStep(ui.mainStatusStep);
      })
      .catch((err) => {
        console.error(err);
        toastError(err?.message || "加载失败");
      })
      .finally(() => setLoading(false));
  }, [orderId]);

  const handleAction = (action: string) => {
    if (action === "cancel-refund") {
      window.location.reload();
      return;
    }
    // 取消订单：复用订单列表的 Dialog.confirm + 真实API
    if (
      action === "cancel" ||
      action === "cancel-free" ||
      action === "cancel-charged"
    ) {
      Dialog.show({
        content: `确认取消订单 ${order?.orderNo || ""}？取消后无法恢复。`,
        closeOnAction: true,
        actions: [
          [
            {
              key: "cancel",
              text: "再想想",
              style: { color: "#FF6B35", fontWeight: 500 },
              onClick: () => {},
            },
            {
              key: "confirm",
              text: "确认取消",
              bold: true,
              style: { color: "#FF6B35", fontWeight: 600 },
              onClick: async () => {
                try {
                  await cancelHotelOrder({
                    id: orderId!,
                    reason: "用户主动取消",
                  });
                  toastSuccess("订单已取消");
                  window.location.reload();
                } catch (err: any) {
                  toastError(err?.message || "取消失败");
                }
              },
            },
          ],
        ],
      });
      return;
    }
    setModal(action);
  };

  /** 再次预订 — 传递产品ID让下单页调API获取真实数据 */
  const handleReorder = () => {
    console.log(rawData.id);
    navigate("/hotel-detail-product", {
      state: {
        hotelId: rawData.hotel.id,
      },
    });
  };

  /** 跳转支付页时携带订单信息 */
  const handleNavigate = (path: string) => {
    if (path === "/payment" && order) {
      navigate("/payment", {
        state: {
          orderNo: order.orderNo ?? "",
          totalAmount: String(order.total),
          type: productType,
        },
      });
    } else {
      navigate(path);
    }
  };

  const subStepsMap: Record<string, string[]> = {
    hotel: ["待酒店确认", "已确认·待入住", "已入住", "已离店"],
    car: ["待派车", "待取车", "使用中", "已还车"],
    route: ["待成团", "已成团·待出发", "行程中", "已完成"],
    guide: ["待确认", "已确认·待服务", "服务中", "已完成"],
  };
  const subSteps = subStepsMap[productType] ?? [];
  const subCurrent = subSteps.findIndex(
    (s) =>
      s === (order?.productSubStatus ?? "") ||
      (order?.productSubStatus ?? "").startsWith(s.split("·")[0]),
  );
  const currentSubStatus = subSteps[subCurrent >= 0 ? subCurrent : 0] ?? "";

  const productTypeLabel: Record<string, string> = {
    hotel: "酒店",
    car: "租车",
    route: "线路",
    guide: "导游",
  };

  const getStatusNote = (status: string) => {
    switch (status) {
      case "待出行":
        return "所有服务待使用";
      case "全部履约":
        return "所有服务已完成，待结算";
      case "已完成":
        return "订单已结算，可评价";
      case "全额取消":
        return "订单已全额取消，可查看详情";
      case "售后中":
        return "售后处理中，可跟进";
      default:
        return null;
    }
  };
  const statusNote = getStatusNote(displayStatus);

  // Loading 状态
  if (!order && loading)
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#F4F5F9" }}
      >
        <div className="flex flex-col items-center gap-3">
          <i
            className="ri-loader-4-line animate-spin"
            style={{ fontSize: 32, color: "#FF6B35" }}
          />
          <span className="text-gray-400">加载中...</span>
        </div>
      </div>
    );

  if (!order && !loading)
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#F4F5F9" }}
      >
        <div className="text-center">
          <p className="text-gray-400 mb-2">无法加载订单信息</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2.5 rounded-full text-sm font-medium cursor-pointer"
            style={{ background: "#FF6B35", color: "#fff" }}
          >
            返回
          </button>
        </div>
      </div>
    );

  const handleContractSigned = () => {
    setContractSigned(true);
    setDisplayStatus("待出行");
    setDisplayStep(3);
    navigate("/contracts");
  };

  return (
    <div
      className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+112px)]"
      style={{
        background: "#F4F5F9",
        fontFamily: "'Inter','SF Pro Text',system-ui,sans-serif",
      }}
    >
      {/* ── 顶部 Header ── */}
      <div
        className="overflow-hidden sticky top-0 z-40"
        style={{
          background:
            "linear-gradient(145deg,#1A2238 0%,#2D3561 60%,#3D5A80 100%)",
        }}
      >
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div
            className="absolute rounded-full"
            style={{
              width: 200,
              height: 200,
              top: -50,
              right: -30,
              background: "rgba(255,107,53,0.1)",
              filter: "blur(40px)",
            }}
          />
        </div>
        <div className="relative px-4 pb-4" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 48px)" }}>
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer flex-shrink-0"
              style={{
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.15)",
              }}
            >
              <i
                className="ri-arrow-left-line"
                style={{ fontSize: 18, color: "#fff" }}
              />
            </button>
            <div className="flex-1 min-w-0">
              <h1
                className="font-bold text-white leading-snug"
                style={{ fontSize: 15 }}
              >
                {order.destination}
                {productTypeLabel[productType]}订单
                <span
                  className="font-normal ml-1.5"
                  style={{ fontSize: 12, color: "rgba(255,255,255,0.45)" }}
                >
                  · 订单号：{order.id}
                </span>
              </h1>
              <div
                className="mt-0.5"
                style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}
              >
                创建时间：{order.time} | 总金额：¥{order.total.toLocaleString()}
              </div>
            </div>
            <span
              className="text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap flex-shrink-0"
              style={{
                background: "rgba(255,255,255,0.12)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.18)",
              }}
            >
              {displayStatus}
            </span>
          </div>
          {statusNote && (
            <div
              className="ml-12 mb-1.5 text-xs"
              style={{ color: "rgba(255,255,255,0.55)" }}
            >
              {statusNote}
            </div>
          )}
          {/* {displayStatus === "待签约" && !contractSigned && (
            <div className="ml-12 mb-1.5">
              <button
                onClick={() => setShowContract(true)}
                className="text-xs px-3 py-1 rounded-full cursor-pointer whitespace-nowrap font-medium"
                style={{
                  background: "rgba(14,165,233,0.25)",
                  color: "#7DD3FC",
                  border: "1px solid rgba(125,211,252,0.3)",
                }}
              >
                去签约
              </button>
            </div>
          )} */}
          <ProgressBarByStep
            step={displayStep}
            titles={["待支付", "待确认", "待入住", "已完成"]}
          />
        </div>
      </div>

      {contractSigned && (
        <div
          className="mx-4 mt-3 px-4 py-3 rounded-2xl flex items-center gap-2"
          style={{ background: "#ECFDF5", border: "1px solid #A7F3D0" }}
        >
          <i
            className="ri-checkbox-circle-fill"
            style={{ color: "#10B981", fontSize: 18 }}
          />
          <span className="text-sm text-green-700 font-medium">
            合同签署成功！订单状态已更新为「待出行」
          </span>
        </div>
      )}

      <InvoiceStatusNotice
        hasInvoice={order.hasInvoice}
        invoiceStatus={order.invoiceStatus}
      />

      {/* 服务通知 */}
      {(displayStatus === "待签约" && !contractSigned) ||
      displayStatus === "待商家确认" ||
      displayStatus === "待出行" ? (
        <div className="mx-4 mt-3 bg-white rounded-2xl p-4">
          {displayStatus === "待签约" && !contractSigned && (
            <div
              className="px-3 py-2.5 rounded-xl flex items-center gap-2"
              style={{ background: "#E0F2FE", border: "1px solid #BAE6FD" }}
            >
              <i
                className="ri-information-line flex-shrink-0"
                style={{ color: "#0EA5E9", fontSize: 14 }}
              />
              <span
                className="text-xs font-medium"
                style={{ color: "#0EA5E9" }}
              >
                请尽快签署合同以确保出行安排
              </span>
            </div>
          )}
          {displayStatus === "待商家确认" && (
            <div
              className="px-3 py-2.5 rounded-xl flex items-center gap-2"
              style={{ background: "#FFF7ED", border: "1px solid #FED7AA" }}
            >
              <i
                className="ri-time-line flex-shrink-0"
                style={{ color: "#EA580C", fontSize: 14 }}
              />
              <span
                className="text-xs font-medium"
                style={{ color: "#EA580C" }}
              >
                商家正在审核，预计1-2小时内确认
              </span>
            </div>
          )}
          {displayStatus === "待出行" && (
            <div
              className="px-3 py-2.5 rounded-xl flex items-center gap-2"
              style={{ background: "#ECFDF5", border: "1px solid #A7F3D0" }}
            >
              <i
                className="ri-checkbox-circle-line flex-shrink-0"
                style={{ color: "#059669", fontSize: 14 }}
              />
              <span
                className="text-xs font-medium"
                style={{ color: "#059669" }}
              >
                一切就绪，祝您旅途愉快！
              </span>
            </div>
          )}
        </div>
      ) : null}

      {/* 产品内容 */}
      <div className="mt-3">
        {productType === "hotel" && (
          <HotelSection
            data={rawData}
            subStatus={currentSubStatus}
            onAction={handleAction}
          />
        )}
      </div>

      {/* 费用明细 */}
      <div className="mx-4 mt-3 bg-white rounded-2xl overflow-hidden">
        <button
          // onClick={() => setShowFee(!showFee)}
          className="w-full flex items-center justify-between px-4 py-3.5 cursor-pointer"
        >
          <span className="text-sm font-bold text-gray-900">支付金额</span>
          <div className="flex items-center gap-2">
            <span className="font-bold text-base" style={{ color: "#EF4444" }}>
              ¥{Number(order.total).toFixed(2)}
            </span>
            {/* <i
              className={
                showFee ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"
              }
              style={{ color: "#9CA3AF", fontSize: 18 }}
            /> */}
          </div>
        </button>
        {showFee && (
          <div
            className="px-4 pb-4 space-y-1.5"
            style={{ borderTop: "1px solid #F5F5F5" }}
          >
            {[
              {
                label: "产品原价",
                value: `¥${(order.total + 50).toLocaleString()}`,
                color: "#374151",
                bold: false,
              },
              {
                label: "优惠券抵扣",
                value: "-¥50",
                color: "#10B981",
                bold: false,
              },
              {
                label: "实付金额",
                value: `¥${order.total.toLocaleString()}`,
                color: "#EF4444",
                bold: true,
              },
            ].map((row) => (
              <div
                key={row.label}
                className="flex justify-between py-2"
                style={{
                  borderBottom: row.bold ? "none" : "1px solid #F8F8F8",
                }}
              >
                <span
                  className={
                    row.bold
                      ? "text-sm font-bold text-gray-900"
                      : "text-xs text-gray-400"
                  }
                >
                  {row.label}
                </span>
                <span
                  className={
                    row.bold ? "text-base font-bold" : "text-xs font-medium"
                  }
                  style={{ color: row.color }}
                >
                  {row.value}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 售后信息：has_refund=1 且 refund_status 为 0（待处理）或 30（已取消）时不展示，其余售后态展示 */}
      {order.hasRefund === 1 &&
        order.refundInfo &&
        order.refundStatus != null &&
        order.refundStatus !== 0 &&
        order.refundStatus !== 30 && (
          <RefundInfoSection refundInfo={order.refundInfo} />
        )}

      <VoucherServiceSection
        onNavigate={(path) => navigate(path)}
        items={[
          {
            icon: "ri-file-text-line",
            label: "电子合同",
            sub: contractSigned ? "已签署" : "待签署",
            color: "#2563EB",
            path: "/contracts",
          },
          {
            icon: "ri-bill-line",
            label: "电子发票",
            sub: "可申请开具",
            color: "#D97706",
            path: "/invoices",
          },
          {
            icon: "ri-customer-service-2-line",
            label: "在线客服",
            sub: "7×24小时",
            color: "#7C3AED",
            path: "/",
          },
        ]}
      />

      {/* 底部操作 */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-white px-4 z-40 flex gap-3"
        style={{ borderTop: "1px solid #F5F5F5", paddingTop: "8px", paddingBottom: "calc(8px + env(safe-area-inset-bottom, 0px))" }}
      >
        <ProductActionButtons
          productType={productType}
          mainStatus={displayStatus}
          mainStatusStep={displayStep}
          hasRefund={order.hasRefund}
          refundStatus={order.refundStatus}
          hasComment={order.hasComment}
          hasInvoice={Number(order.hasInvoice) || 0}
          onAction={handleAction}
          onNavigate={handleNavigate}
          onReorder={handleReorder}
        />
      </div>

      {/* ── 弹窗群 ── */}
      {showContract && (
        <ContractSignModal
          onClose={() => setShowContract(false)}
          onSign={handleContractSigned}
        />
      )}
      {modal === "cancel-free" && (
        <CancelOrderModal
          type="free"
          productName="三亚亚龙湾瑞吉度假酒店"
          onClose={() => setModal(null)}
          onConfirm={() => {
            setModal(null);
            setDisplayStatus("全额取消");
          }}
        />
      )}
      {modal === "cancel-charged" && (
        <CancelOrderModal
          type="charged"
          productName="三亚亚龙湾瑞吉度假酒店"
          roomType="豪华海景大床房"
          quantity={1}
          totalAmount={order.total}
          refundAmount={Math.round(order.total * 0.7)}
          policy="入住前1-3天取消，退款70%，扣除首晚房费作为手续费。"
          onClose={() => setModal(null)}
          onConfirm={() => {
            setModal(null);
            setDisplayStatus("退款中");
          }}
        />
      )}
      {modal === "cancel-route" && (
        <RouteRefundModal
          status="待成团"
          onClose={() => setModal(null)}
          onConfirm={() => {
            setModal(null);
            setDisplayStatus("全额取消");
          }}
        />
      )}
      {modal === "cancel-route-charged" && (
        <RouteRefundModal
          status="已成团·待出发"
          onClose={() => setModal(null)}
          onConfirm={() => {
            setModal(null);
            setDisplayStatus("退款中");
          }}
        />
      )}
      {modal === "afterSale" && (
        <AfterSaleModal
          visible={true}
          onClose={() => setModal(null)}
          productName={order.productName}
          orderId={orderId}
          orderType={productType}
          orderAmount={order.total}
        />
      )}
      {modal === "review" && (
        <ReviewModal
          visible={true}
          onClose={() => setModal(null)}
          productName={order.productName}
          orderId={orderId}
          productId={rawData?.hotel?.id}
          productType={productType}
        />
      )}
      {modal === "invoice" && orderId != null && (
        <InvoiceModal
          orderId={orderId}
          productType="hotel"
          productTitle={order.productName}
          productSubtitle={order.productSpec}
          invoiceAmount={order.total}
          onClose={() => setModal(null)}
          onSubmitSuccess={() => setModal(null)}
        />
      )}
      {modal === "location" && (
        <CarLocationModal onClose={() => setModal(null)} />
      )}
      {modal === "change-pickup" && (
        <ChangePickupModal onClose={() => setModal(null)} />
      )}
    </div>
  );
}
